import React, { useState } from 'react'
import { Route, Link } from 'react-router-dom'
import classNames from 'classnames'
import LandingHeaderText from '#src/components/LandingHeaderText'
import ExpertCards from './components/ExpertCards'
import ExpertPlayer from './components/ExpertPlayer'
import MeetTheExperts from './components/MeetTheExperts'
import FeaturedReviews from '#src/components/FeaturedReviews'
import HowItWorks from '#src/components/HowItWorks'
import Header from '#src/components/Header'
import CTABar from './components/CTABar'
import numeral from 'numeral'
import Footer from '#src/components/Footer'
import backgroundImage from '#src/assets/images/background.jpg'
import sortBy from 'lodash/sortBy'
import useBreakpoint from '#src/hooks/useBreakpoint'
import { animateScroll } from 'react-scroll'
import ArrowLeft from './arrow-left.svg'
import ArrowRight from './arrow-right.svg'
import SchemaData from './components/SchemaData'
import {
  SpotlightExpertsComponent,
  SpotlightExpertFragment,
  HomepageReviewCountsComponent
} from '#src/generated/graphql'
import PathDropdown from '#src/components/PathDropdown'
import DismissIcon from './dismiss.svg'
import styles from './styles.css'
import PageContent from '#src/components/PageContent'
import BackgroundImage from '#src/components/BackgroundImage'
import { Button } from '@deal/components'

interface TopWrapperProps {
  featuredExperts: SpotlightExpertFragment[]
  featuredExpert?: SpotlightExpertFragment
  isExpertPlayerVisible: boolean
  onDismissVideo: () => void
  onSetFeaturedExpert: (featuredExpert: SpotlightExpertFragment) => void
  onClickExpertCard: (featuredExpert: SpotlightExpertFragment) => void
}

const TopWrapper: React.SFC<TopWrapperProps> = ({
  featuredExperts,
  featuredExpert,
  isExpertPlayerVisible,
  onDismissVideo,
  onClickExpertCard,
  onSetFeaturedExpert
}) => {
  const SPOTLIGHT_EXPERT_COUNT = featuredExperts.length
  const [currentExpertIndex, setCurrentExpertIndex] = useState<number>(0)

  const onNextExpert = () => {
    const nextIndex = currentExpertIndex + 1
    setCurrentExpertIndex(nextIndex)
    onSetFeaturedExpert(featuredExperts[nextIndex])
  }

  const onPreviousExpert = () => {
    const prevIndex = currentExpertIndex - 1

    setCurrentExpertIndex(prevIndex)
    onSetFeaturedExpert(featuredExperts[prevIndex])
  }

  const canGoToNextExpert = currentExpertIndex < SPOTLIGHT_EXPERT_COUNT - 1
  const canGoToPrevExpert = currentExpertIndex > 0

  return (
    <BackgroundImage
      src={backgroundImage}
      lazyLoad={false}
      size="100vw"
      containerClassName={classNames(styles.container, {
        [styles.playerVisible]: isExpertPlayerVisible
      })}
      contentClassName={styles.content}
    >
      {isExpertPlayerVisible && (
        <>
          {canGoToPrevExpert && (
            <ArrowLeft onClick={onPreviousExpert} className={styles.largeArrowLeft} />
          )}
          <ExpertPlayer
            key={featuredExpert && featuredExpert.id}
            onNextExpert={onNextExpert}
            canGoToPrevExpert={canGoToPrevExpert}
            canGoToNextExpert={canGoToNextExpert}
            onPreviousExpert={onPreviousExpert}
            featuredExpert={featuredExpert}
            onDismissVideo={() => {
              onDismissVideo()
              setCurrentExpertIndex(0)
            }}
          />
          {canGoToNextExpert && (
            <ArrowRight onClick={onNextExpert} className={styles.largeArrowRight} />
          )}
        </>
      )}

      <LandingHeaderText actions={<PathDropdown sourceKey="logged-out-home-category-sheet" />} />

      <ExpertCards
        featuredExperts={featuredExperts}
        isExpertPlayerVisible={isExpertPlayerVisible}
        onClickExpertCard={(featuredExpert: SpotlightExpertFragment, idx: number) => {
          onClickExpertCard(featuredExpert)
          setCurrentExpertIndex(idx)
        }}
      />
    </BackgroundImage>
  )
}

const Home: React.SFC = () => {
  const [isExpertPlayerVisible, setIsExpertPlayerVisible] = useState(false)
  const [featuredExpert, setFeaturedExpert] = useState<SpotlightExpertFragment | undefined>(
    undefined
  )

  const isLargerThanSm = useBreakpoint('sm')
  const shouldHideHeader = !isLargerThanSm && isExpertPlayerVisible

  return (
    <Route
      render={({ location, history }) => {
        /**
         * This allows for back button movement away from the expert video player
         *
         * 1. If the video slug is there but the player isn't open
         *  1a. Open it if there is a featured expert set (unlikely)
         *  1b. Remove the slug since it shouldn't be there
         * 2. If the slug isn't there but the player is visible, hide the player
         */
        const shouldExpertPlayerBeVisible = location.hash === '#video'
        if (shouldExpertPlayerBeVisible && !isExpertPlayerVisible) {
          if (featuredExpert) {
            setIsExpertPlayerVisible(true)
            animateScroll.scrollToTop()
          } else {
            const locationWithSlug = `${location.pathname}${location.search}`
            history.push(locationWithSlug)
          }
        } else if (!shouldExpertPlayerBeVisible && isExpertPlayerVisible) {
          setIsExpertPlayerVisible(false)
          setFeaturedExpert(undefined)
        }

        return (
          <>
            <SchemaData />
            {!shouldHideHeader && (
              <Header isDepartmentSpecificPage={false} reserveSpace={false} transparent={true} />
            )}

            {isExpertPlayerVisible && (
              <PageContent className={styles.dismissWrapper}>
                <div
                  className={styles.dismiss}
                  onClick={() => {
                    setFeaturedExpert(undefined)
                    setIsExpertPlayerVisible(false)

                    const locationWithSlug = `${location.pathname}${location.search}`
                    history.push(locationWithSlug)
                  }}
                >
                  <DismissIcon className={styles.dismissIcon} />
                </div>
              </PageContent>
            )}

            <SpotlightExpertsComponent
              variables={{
                query: {
                  departmentIds: [],
                  facetedSearch: true,
                  facets: [
                    {
                      name: 'spotlight',
                      bucketValues: ['YES']
                    }
                  ]
                }
              }}
            >
              {({ data }) => {
                if (data && data.expertSearchConnection) {
                  const featuredExperts = data.expertSearchConnection.edges.map(edge => edge.node)

                  return (
                    <TopWrapper
                      featuredExperts={featuredExperts}
                      featuredExpert={featuredExpert}
                      isExpertPlayerVisible={isExpertPlayerVisible}
                      onSetFeaturedExpert={(featuredExpert: SpotlightExpertFragment) => {
                        setFeaturedExpert(featuredExpert)
                      }}
                      onDismissVideo={() => {
                        setFeaturedExpert(undefined)
                        setIsExpertPlayerVisible(false)

                        const locationWithSlug = `${location.pathname}${location.search}`
                        history.push(locationWithSlug)
                      }}
                      onClickExpertCard={(featuredExpert: SpotlightExpertFragment) => {
                        setFeaturedExpert(featuredExpert)
                        setIsExpertPlayerVisible(true)
                        animateScroll.scrollToTop()

                        const locationWithSlug = `${location.pathname}${location.search}#video`
                        history.push(locationWithSlug)
                      }}
                    />
                  )
                }

                return null
              }}
            </SpotlightExpertsComponent>

            <div className={styles.boxWrapper}>
              <CTABar
                featuredExpert={featuredExpert}
                isExpertPlayerVisible={isExpertPlayerVisible}
              />
              <MeetTheExperts />
              <HowItWorks
                actions={
                  <>
                    <Link to="/how-it-works" className={styles.buttonLink}>
                      <Button variant="secondary-outline" className={styles.button}>
                        Learn more
                      </Button>
                    </Link>
                    <Link to="/in-action" className={styles.buttonLink}>
                      <Button variant="secondary-outline" className={styles.button}>
                        See Curated in action
                      </Button>
                    </Link>
                  </>
                }
              />
            </div>
            <HomepageReviewCountsComponent>
              {({ data }) => {
                if (data && data.consumerExpertReviewConnection) {
                  const reviewSummaryBuckets = data.consumerExpertReviewConnection.facets.find(
                    facet => facet.name === 'rating'
                  )

                  let subHeader = 'Curated Experts have 4,630+ reviews with an average of 4.8 stars'
                  if (reviewSummaryBuckets && 'buckets' in reviewSummaryBuckets) {
                    const reviewBuckets = sortBy(reviewSummaryBuckets.buckets, [
                      bucket => bucket.value
                    ])
                    const totalReviews = reviewBuckets.reduce(
                      (sum, bucket) => sum + bucket.count,
                      0
                    )

                    const averageRating =
                      reviewBuckets.reduce(
                        (sum, bucket) => sum + parseInt(bucket.value, 10) * bucket.count,
                        0
                      ) / totalReviews

                    subHeader = `Curated experts have ${numeral(totalReviews).format(
                      '0,0'
                    )}+ reviews with an average rating of ${averageRating.toFixed(1)} stars`
                  }

                  return (
                    <FeaturedReviews
                      forceFullGrid={true}
                      header="People love Curated Experts"
                      subHeader={subHeader}
                      spotlightOnly={true}
                      className={styles.featuredReviewWrapper}
                    />
                  )
                }
                return null
              }}
            </HomepageReviewCountsComponent>
            <Footer isDepartmentSpecificPage={false} />
          </>
        )
      }}
    />
  )
}

export default Home
